import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Paginated amenities list
//
// The image panel has a fixed height. The list panel inherits that same row
// height. If the list of items would render taller than the visible area,
// we slice it into pages and show prev/next controls.
//
// Approach: a hidden "measurement" copy of the full list is rendered behind
// the visible list with the same width/layout but with no height constraint.
// We compare its natural scrollHeight to the available container height to
// decide how many items fit per page.
// ============================================================================

interface PaginatedAmenitiesListProps {
  items: Amenity[];
}

function PaginatedAmenitiesList({ items }: PaginatedAmenitiesListProps) {
  const [itemsPerPage, setItemsPerPage] = useState<number>(items.length || 1);
  const [page, setPage] = useState(0);

  const measureRef = useRef<HTMLUListElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (items.length === 0) {
      setItemsPerPage(1);
      return;
    }

    const measure = () => {
      const list = measureRef.current;
      const inner = innerRef.current;
      if (!list || !inner) return;
      const naturalH = list.scrollHeight;
      const availH = inner.clientHeight;
      if (availH <= 0) return;
      if (naturalH <= availH + 2) {
        setItemsPerPage(items.length);
        return;
      }
      // Approximate items-per-page from the height ratio. Items have similar
      // (but not identical) heights, so leave a small safety margin so the
      // last item on a page doesn't get clipped.
      const ratio = Math.max(0.05, (availH - 8) / naturalH);
      const ipp = Math.max(2, Math.floor(items.length * ratio));
      setItemsPerPage(ipp);
    };

    measure();
    setPage(0);

    if (typeof ResizeObserver !== 'undefined' && innerRef.current) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(innerRef.current);
      return () => ro.disconnect();
    }
  }, [items.length]);

  const safeItemsPerPage = Math.max(1, itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(items.length / safeItemsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * safeItemsPerPage,
    (safePage + 1) * safeItemsPerPage
  );

  return (
    <div className="amenities-list-area">
      <div ref={innerRef} className="amenities-list-area-inner">
        {/* Hidden measurement pass: full list rendered with no height cap so
            we can read its natural scrollHeight. */}
        <ul
          ref={measureRef}
          className="amenities-col-list amenities-col-list--measure"
          aria-hidden="true"
        >
          {items.map((item, i) => (
            <li key={`m-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>

        {/* Visible (paginated) list */}
        <ul className="amenities-col-list">
          {currentItems.map((item, i) => (
            <li key={`p${safePage}-${i}`} className="amenities-col-item">
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      {totalPages > 1 && (
        <div className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  imagePosition: 'left' | 'right';
}

function AmenityColumn({ headingNode, headingText, items, photos, imagePosition }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      <PaginatedAmenitiesList items={items} />
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      <PhotoSlider photos={photos} heading={headingText} />
    </div>
  ) : null;

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Luxuries';

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-stack">
            {communityItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community Amenities</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={communityHeadingText}
                items={communityItems}
                photos={communityPhotos}
                imagePosition="left"
              />
            )}
            {apartmentItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Apartment Heading"
                    defaultValue="<p>Interior Luxuries</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={apartmentHeadingText}
                items={apartmentItems}
                photos={apartmentPhotos}
                imagePosition="right"
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Stacked rows container */
        .amenities-stack {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        /* Each row: side-by-side image + content with a fixed height so the
           image gallery never grows tall. The same layout is used on tablet
           and mobile — the row simply gets shorter on smaller viewports. */
        .amenities-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          background: white;
          overflow: hidden;
          height: 460px;
        }
        .amenities-row--no-image {
          grid-template-columns: 1fr;
          height: auto;
          min-height: 220px;
        }

        /* Content panel */
        .amenities-row-content {
          display: flex;
          flex-direction: column;
          padding: 0;
          min-height: 0;
        }

        /* Image panel fills its cell */
        .amenities-row-image {
          position: relative;
          overflow: hidden;
          height: 100%;
        }
        .amenities-row-image .amenities-slider {
          height: 100%;
        }

        /* Tablet: keep side-by-side layout, just slightly shorter row. */
        @media (max-width: 900px) {
          .amenities-row {
            height: 420px;
          }
        }

        /* Mobile: stack image on top, content below — but give the content
           area a fixed height so pagination still has room to detect
           overflow and slice the list into pages. */
        @media (max-width: 768px) {
          .amenities-row {
            grid-template-columns: 1fr;
            grid-template-rows: 220px 360px;
            height: auto;
          }
          .amenities-row--no-image {
            grid-template-rows: auto;
            min-height: 220px;
          }
          /* Image on top regardless of the desktop stagger. */
          .amenities-row--img-right > .amenities-row-content {
            order: 2;
          }
          .amenities-row--img-right > .amenities-row-image {
            order: 1;
          }
          .amenities-row-image {
            height: 220px;
          }
          .amenities-row-image .amenities-slider {
            height: 220px;
          }
        }

        @media (max-width: 600px) {
          .amenities-row {
            grid-template-rows: 200px 340px;
          }
          .amenities-row-image,
          .amenities-row-image .amenities-slider {
            height: 200px;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          text-align: left;
          padding: 1.5rem 2rem 0.25rem;
          margin: 0;
          letter-spacing: 0.02em;
        }

        @media (max-width: 900px) {
          .amenities-col-heading {
            font-size: 1.5rem;
            padding: 1rem 1rem 0.25rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-heading {
            font-size: 1.15rem;
            padding: 0.75rem 0.75rem 0.25rem;
            letter-spacing: 0.01em;
            text-align: center;
          }
        }

        /* List area takes the remaining vertical space inside the row */
        .amenities-list-area {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        .amenities-list-area-inner {
          flex: 1;
          position: relative;
          overflow: hidden;
          min-height: 0;
        }

        .amenities-col-list {
          list-style: none;
          padding: 0.75rem 2rem 0.75rem;
          margin: 0;
          /* Two-column grid keeps overflow detection simple. */
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.25rem 1.5rem;
          align-content: start;
        }
        /* Hidden measurement copy: full list, no height cap. */
        .amenities-col-list--measure {
          position: absolute;
          inset: 0;
          visibility: hidden;
          pointer-events: none;
          height: auto;
        }

        /* Tablet: keep two columns, tighten spacing. */
        @media (max-width: 900px) {
          .amenities-col-list {
            padding: 0.5rem 1rem 0.5rem;
            gap: 0.2rem 0.75rem;
          }
        }
        /* Mobile: keep two columns side-by-side, even tighter. */
        @media (max-width: 600px) {
          .amenities-col-list {
            padding: 0.4rem 0.75rem 0.4rem;
            gap: 0.15rem 0.5rem;
          }
        }

        .amenities-col-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
        }

        @media (max-width: 900px) {
          .amenities-col-item {
            font-size: 0.8rem;
            padding: 0.2rem 0;
            gap: 0.4rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-item {
            font-size: 0.72rem;
            padding: 0.15rem 0;
            gap: 0.3rem;
            line-height: 1.35;
          }
        }

        .amenities-col-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        /* Pagination controls — only rendered when list overflows */
        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 0.25rem 2rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text);
        }
        @media (max-width: 900px) {
          .amenities-pagination {
            padding: 0.25rem 1rem 0.75rem;
            gap: 0.4rem;
            font-size: 0.78rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-pagination {
            padding: 0.25rem 0.75rem 0.6rem;
            gap: 0.35rem;
            font-size: 0.72rem;
          }
          .amenities-pagination-btn {
            width: 24px;
            height: 24px;
          }
          .amenities-pagination-info {
            min-width: 2.5rem;
          }
        }
        .amenities-pagination-btn {
          width: 28px;
          height: 28px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid rgba(0, 0, 0, 0.15);
          background: white;
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: white;
        }
        .amenities-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .amenities-pagination-info {
          min-width: 3rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Luxuries</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
